/**
 * ==========================================================================
 *  Home.jsx — Beratungs-Cockpit-Startseite
 * ==========================================================================
 *  Erste Ansicht nach dem Login (und ueber Klick aufs Logo erreichbar).
 *
 *  Drei Sektionen:
 *    1. Mini-Statistik (Kunden gesamt, offene ToDos, Termine diese Woche)
 *    2. Termine: heute / diese Woche / naechste Woche
 *    3. Offene ToDos: gruppiert (ueberfaellig / heute / diese Woche /
 *       spaeter / ohne Datum), mit Kunden-Name
 *
 *  Klick auf einen Termin- oder ToDo-Eintrag springt ins Berater-
 *  Dashboard mit dem entsprechenden Kunden ausgewaehlt.
 *
 *  Props:
 *    onOpenKunde(kundeId)   — Parent wechselt View und setzt den Kunden.
 * ==========================================================================
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  Calendar as CalendarIcon, CheckSquare, Users, Loader2,
  Activity, AlertTriangle, Video, MapPin, BarChart3, Sparkles, Check, Search,
  MessageSquare, Package, Inbox, Edit3, Plus, ArrowRight, Printer,
} from 'lucide-react';
import { api, ApiError } from '../api';
import KundenKarte from '../components/KundenKarte';

const CI = { brombeer: '#85366C', orchidee: '#DC91D0', blue: '#2DBAE8', green: '#8CC53F', red: '#ED2979' };

const WOCHENTAGE_KURZ = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONATE_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const formatTagRelativ = (datumISO) => {
  if (!datumISO) return '';
  const d = new Date(datumISO + 'T00:00:00');
  const heute = new Date(); heute.setHours(0, 0, 0, 0);
  const diff = Math.round((d - heute) / 86400000);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  if (diff === -1) return 'Gestern';
  return `${WOCHENTAGE_KURZ[d.getDay()]}, ${d.getDate()}. ${MONATE_KURZ[d.getMonth()]}`;
};

// Liefert ISO-Datum 'YYYY-MM-DD' fuer ein Date-Offset relativ zu heute.
const isoOffset = (tage) => {
  const d = new Date();
  d.setDate(d.getDate() + tage);
  return d.toISOString().slice(0, 10);
};

// Montag der aktuellen Woche (lokal).
const montagDieserWoche = () => {
  const d = new Date();
  const dow = d.getDay();          // 0=So, 1=Mo, ..., 6=Sa
  const mo = new Date(d);
  mo.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  mo.setHours(0, 0, 0, 0);
  return mo;
};

export default function Home({ onOpenKunde, onOpenAnfrage }) {
  const [todos, setTodos] = useState([]);
  const [termine, setTermine] = useState([]);
  const [neueTermine, setNeueTermine] = useState([]);  // in den letzten 7 Tagen ANGELEGTE Termine
  const [kundenZahl, setKundenZahl] = useState(null);
  const [alleKunden, setAlleKunden] = useState([]);
  const [pcStats, setPcStats] = useState(null);   // {gesamt, liane_box_kunden, matched}
  const [lianeStats, setLianeStats] = useState(null);  // {gesamt, aktiv, pause, inaktiv, auch_beratungskunden}
  const [neueAnfragen, setNeueAnfragen] = useState([]); // letzte 7 Tage + ungelesene
  const [aktiveTouren, setAktiveTouren] = useState([]);
  const [doiPending, setDoiPending] = useState([]); // Booking-Widget-Kunden ohne DOI
  const [suchQuery, setSuchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      try {
        // Termine: -7 Tage bis +30 Tage (Heute, diese und naechste Woche
        // sicher abgedeckt). ToDos: alle offenen. Plus die in den letzten
        // 7 Tagen ANGELEGTEN Termine — kann auch Termine in 6 Monaten
        // umfassen, die vor zwei Tagen ins System kamen.
        const von = isoOffset(-7);
        const bis = isoOffset(30);
        const [t, neuT, td, ku, pc, anfr, tou, doi, liane] = await Promise.all([
          api.get(`/api/termine?von=${von}&bis=${bis}`),
          api.get('/api/termine?neu_seit_tage=7').catch(() => []),
          api.get('/api/todos?offen=true'),
          api.get('/api/kunden').catch(() => null),
          api.get('/api/pflegecockpit/stats').catch(() => null),
          api.get('/api/feedback/anfragen?neu_seit_tage=7&limit=12').catch(() => []),
          api.get('/api/touren?modus=aktiv').catch(() => []),
          api.get('/api/kunden/doi-pending').catch(() => []),
          api.get('/api/liane/kunden/stats').catch(() => null),
        ]);
        if (abgebrochen) return;
        setTermine(t || []);
        setNeueTermine(neuT || []);
        setTodos(td || []);
        if (Array.isArray(ku)) {
          setKundenZahl(ku.length);
          setAlleKunden(ku);
        }
        if (pc) setPcStats(pc);
        if (liane) setLianeStats(liane);
        setNeueAnfragen(Array.isArray(anfr) ? anfr : []);
        setAktiveTouren(Array.isArray(tou) ? tou : []);
        setDoiPending(Array.isArray(doi) ? doi : []);
      } catch (err) {
        if (abgebrochen) return;
        const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
        setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    })();
    return () => { abgebrochen = true; };
  }, []);

  // Klick auf den Erledigt-Haken in einem ToDo-Eintrag: das ToDo per API
  // schliessen und lokal aus der Liste entfernen, ohne dass der Berater
  // erst in die Kundenakte muss. Toleriert API-Fehler stillschweigend
  // (UI-Komfort-Aktion, kein Datenverlust-Risiko).
  const handleTodoErledigt = async (todoId) => {
    try {
      await api.put(`/api/todos/${todoId}`, { erledigt: true });
      setTodos(prev => prev.filter(t => t.id !== todoId));
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert('To-do konnte nicht als erledigt markiert werden: ' + msg);
    }
  };

  // Eingabe-Form fuer uebergeordnete Aufgaben (kunde_id=null).
  const [neueAufgabeText, setNeueAufgabeText] = useState('');
  const [neueAufgabeFaellig, setNeueAufgabeFaellig] = useState('');
  const [neueAufgabePrio, setNeueAufgabePrio] = useState('B');
  const [isSavingAufgabe, setIsSavingAufgabe] = useState(false);

  const handleNeueAufgabe = async () => {
    const text = neueAufgabeText.trim();
    if (!text) return;
    setIsSavingAufgabe(true);
    try {
      const neu = await api.post('/api/todos', {
        text,
        faellig_am: neueAufgabeFaellig || null,
        prioritaet: neueAufgabePrio || 'B',
      });
      setTodos(prev => [{ ...neu, kunde_name: null }, ...prev]);
      setNeueAufgabeText('');
      setNeueAufgabeFaellig('');
      setNeueAufgabePrio('B');
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert('Aufgabe konnte nicht angelegt werden: ' + msg);
    } finally {
      setIsSavingAufgabe(false);
    }
  };

  // Inline-Edit aus der Home-Liste: Text / Datum / Prioritaet veraendern,
  // ohne in die Akte zu wechseln. Patch enthaelt nur geaenderte Felder.
  const handleTodoUpdate = async (todoId, patch) => {
    try {
      const aktualisiert = await api.put(`/api/todos/${todoId}`, patch);
      setTodos(prev => prev.map(t => t.id === todoId ? { ...t, ...aktualisiert } : t));
      return aktualisiert;
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert('Aktualisierung fehlgeschlagen: ' + msg);
      throw err;
    }
  };

  // Aus dem Pin-Popup auf der Karte: Kunde zu einer Tour hinzufuegen.
  // Status 'planung' (default) — Bestaetigt + Uhrzeit kommt im Tourplan.
  // Bei 409 (Kunde schon auf einer aktiven Tour) zeigen wir die Backend-
  // Meldung im Klartext, damit Hendrik sieht, wo der Eintrag liegt.
  const handleAddKundeZuTour = async (kundeId, tourId) => {
    try {
      await api.post(`/api/touren/${tourId}/kunden`, { kunde_id: kundeId, status: 'planung' });
      const tour = aktiveTouren.find(t => t.id === tourId);
      const tourLabel = tour ? `${tour.name} (${tour.datum})` : 'der Tour';
      alert(`Hinzugefuegt zu Tour „${tourLabel}".`);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert('Konnte nicht hinzugefuegt werden: ' + msg);
    }
  };

  // --- Termin-Buckets ---
  const termineGruppen = useMemo(() => {
    const heuteISO = new Date().toISOString().slice(0, 10);
    const mo = montagDieserWoche();
    const so = new Date(mo); so.setDate(mo.getDate() + 6); so.setHours(23, 59, 59, 999);
    const moNaechste = new Date(mo); moNaechste.setDate(mo.getDate() + 7);
    const soNaechste = new Date(moNaechste); soNaechste.setDate(moNaechste.getDate() + 6);

    const heute = [];
    const dieseWoche = [];
    const naechsteWoche = [];
    for (const t of (termine || [])) {
      if (!t.date) continue;
      const d = new Date(t.date + 'T00:00:00');
      if (t.date === heuteISO) heute.push(t);
      else if (d >= mo && d <= so) dieseWoche.push(t);
      else if (d >= moNaechste && d <= soNaechste) naechsteWoche.push(t);
    }
    return { heute, dieseWoche, naechsteWoche };
  }, [termine]);

  // --- ToDo-Buckets ---
  // Innerhalb jedes Buckets: erst Prioritaet (A>B>C), dann Faelligkeit (frueher
  // zuerst). So sieht der Berater dringende A-Aufgaben oben in jeder Gruppe.
  const todosGruppen = useMemo(() => {
    const heuteISO = new Date().toISOString().slice(0, 10);
    const so = new Date(montagDieserWoche()); so.setDate(so.getDate() + 6);
    const sonntagISO = so.toISOString().slice(0, 10);

    const ueberfaellig = [], heute = [], dieseWoche = [], spaeter = [], ohneDatum = [];
    for (const t of (todos || [])) {
      if (t.erledigt) continue;
      if (!t.faellig_am) { ohneDatum.push(t); continue; }
      if (t.faellig_am < heuteISO) ueberfaellig.push(t);
      else if (t.faellig_am === heuteISO) heute.push(t);
      else if (t.faellig_am <= sonntagISO) dieseWoche.push(t);
      else spaeter.push(t);
    }
    const prioRang = { A: 0, B: 1, C: 2 };
    const sortiere = (liste) => liste.slice().sort((a, b) => {
      const pa = prioRang[(a.prioritaet || 'B').toUpperCase()] ?? 1;
      const pb = prioRang[(b.prioritaet || 'B').toUpperCase()] ?? 1;
      if (pa !== pb) return pa - pb;
      const da = a.faellig_am || '9999-12-31';
      const db = b.faellig_am || '9999-12-31';
      return da.localeCompare(db);
    });
    return {
      ueberfaellig: sortiere(ueberfaellig),
      heute: sortiere(heute),
      dieseWoche: sortiere(dieseWoche),
      spaeter: sortiere(spaeter),
      ohneDatum: sortiere(ohneDatum),
    };
  }, [todos]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Lade Übersicht…
      </div>
    );
  }

  if (fehler) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">{fehler}</div>
      </div>
    );
  }

  const offeneTodos = (todos || []).filter(t => !t.erledigt).length;
  const termineDieseWoche = termineGruppen.heute.length + termineGruppen.dieseWoche.length;

  const handleDruckeToDos = () => {
    const heuteAnzeige = new Date().toLocaleDateString('de-DE', {
      weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
    });
    const renderGruppe = (titel, eintraege) => {
      if (!eintraege || eintraege.length === 0) return '';
      const zeilen = eintraege.map(t => {
        const kunde = t.kunde_id ? (t.kunde_name || '—') : 'Allgemein';
        const datum = t.faellig_am
          ? new Date(t.faellig_am + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
          : '—';
        const prio = (t.prioritaet || 'B').toUpperCase();
        const text = (t.text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return `<tr>
            <td class="prio prio-${prio}">${prio}</td>
            <td class="datum">${datum}</td>
            <td class="kunde">${kunde}</td>
            <td class="text"><span class="checkbox">☐</span> ${text}</td>
          </tr>`;
      }).join('');
      return `
        <h2 class="gruppe">${titel} <span class="zaehler">(${eintraege.length})</span></h2>
        <table>
          <thead>
            <tr><th class="prio">Prio</th><th class="datum">Datum</th><th class="kunde">Kunde</th><th>Aufgabe</th></tr>
          </thead>
          <tbody>${zeilen}</tbody>
        </table>`;
    };

    const html = `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8" />
<title>To-do-Liste vom ${heuteAnzeige}</title>
<style>
  body { font-family: -apple-system, Segoe UI, Helvetica, Arial, sans-serif; color: #111; padding: 24px; max-width: 800px; margin: 0 auto; }
  h1 { color: #85366C; margin: 0 0 4px 0; font-size: 22px; }
  .stand { color: #555; font-size: 12px; margin-bottom: 18px; }
  .gruppe { color: #85366C; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px; margin: 18px 0 6px 0; border-bottom: 1px solid #d4a8c4; padding-bottom: 3px; }
  .zaehler { color: #888; font-weight: normal; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 13px; }
  th { text-align: left; padding: 4px 6px; background: #f6efef; border-bottom: 1px solid #ccc; font-size: 11px; color: #555; text-transform: uppercase; }
  td { padding: 6px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
  td.prio { width: 36px; text-align: center; font-weight: bold; }
  td.datum { width: 70px; color: #555; font-variant-numeric: tabular-nums; }
  td.kunde { width: 160px; color: #333; }
  td.text { color: #111; }
  .prio-A { color: #b91c1c; }
  .prio-B { color: #a16207; }
  .prio-C { color: #1d4ed8; }
  .checkbox { display: inline-block; width: 14px; color: #888; }
  .leer { color: #888; font-style: italic; padding: 12px 0; }
  @media print {
    body { padding: 0; }
    .keine-druck { display: none; }
    @page { margin: 1.5cm; }
  }
</style>
</head>
<body>
  <h1>To-do-Liste — Pflegeberatung</h1>
  <div class="stand">Stand: ${heuteAnzeige}</div>
  ${renderGruppe('Überfällig', todosGruppen.ueberfaellig)}
  ${renderGruppe('Heute fällig', todosGruppen.heute)}
  ${renderGruppe('Diese Woche', todosGruppen.dieseWoche)}
  ${renderGruppe('Später', todosGruppen.spaeter)}
  ${renderGruppe('Ohne Datum', todosGruppen.ohneDatum)}
  ${offeneTodos === 0 ? '<div class="leer">Keine offenen Aufgaben.</div>' : ''}
  <script>window.onload = function(){ window.print(); };</script>
</body>
</html>`;
    const w = window.open('', '_blank');
    if (!w) { alert('Bitte Pop-up-Blocker für diese Seite erlauben.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="bg-slate-50 min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ===== Kunden-Schnellsuche (ganz oben, prominent) =====
            Direkt-Sprung in die Akte: Name eintippen, klick auf
            Treffer → springt ins Berater-Dashboard mit selektiertem
            Kunden, ohne Umweg ueber das volle Dashboard. */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: CI.brombeer }}>
            <Search className="w-4 h-4" /> Kunde finden
          </h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={suchQuery}
              onChange={(e) => setSuchQuery(e.target.value)}
              placeholder="Name, PLZ, Ort, Tour…"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-300 bg-white"
            />
          </div>
          {suchQuery.trim().length >= 2 && (() => {
            const q = suchQuery.trim().toLowerCase();
            const treffer = (alleKunden || []).filter(k => {
              const t = `${k.firstName || ''} ${k.lastName || ''} ${k.zip || ''} ${k.city || ''} ${k.tour || ''}`.toLowerCase();
              const angeh = k.relative ? `${k.relative.firstName || ''} ${k.relative.lastName || ''}`.toLowerCase() : '';
              return t.includes(q) || angeh.includes(q);
            }).slice(0, 12);
            if (treffer.length === 0) {
              return <div className="mt-3 text-sm text-gray-500 italic">Keine Treffer.</div>;
            }
            return (
              <ul className="mt-3 divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                {treffer.map(k => (
                  <li key={k.id}>
                    <button
                      onClick={() => onOpenKunde && onOpenKunde(k.id)}
                      className="w-full text-left px-4 py-2.5 hover:bg-fuchsia-50 transition-colors flex items-center justify-between gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-800 truncate">
                          {[k.lastName, k.firstName].filter(Boolean).join(', ') || 'Unbenannt'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {[k.zip, k.city].filter(Boolean).join(' ')}
                          {k.relative && (k.relative.firstName || k.relative.lastName) && (
                            <> · über Angehörigen: {k.relative.firstName} {k.relative.lastName}</>
                          )}
                        </div>
                      </div>
                      <span className="text-xs font-bold text-brombeer flex-shrink-0" style={{ color: CI.brombeer }}>
                        Akte öffnen →
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            );
          })()}
        </section>

        {/* ===== Oberer Bereich: ToDo-Liste prominent links, Stats schmal rechts =====
            Layout-Wechsel: bisherige 3-Spalten-Statistik wurde aufgeloest.
            Hendrik nutzt die KundenSammelKachel selten — sie wird kompakt
            in die rechte Seitenleiste verschoben, damit die ToDo-Liste links
            den Hauptplatz bekommt (taegliches Arbeitsmittel).            */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* ToDo-Liste — Hauptarbeitsbereich, 2/3 Breite */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: CI.brombeer }}>
                <CheckSquare className="w-4 h-4" /> Offene To-dos
              </h2>
              <button
                onClick={handleDruckeToDos}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
                title="To-do-Liste drucken (oeffnet Druckansicht in neuem Fenster)"
              >
                <Printer className="w-3.5 h-3.5" /> Drucken
              </button>
            </div>

            {/* Inline-Add fuer uebergeordnete Aufgaben */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4 items-stretch sm:items-center">
              <input
                type="text"
                value={neueAufgabeText}
                onChange={(e) => setNeueAufgabeText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleNeueAufgabe(); } }}
                placeholder="Neue übergeordnete Aufgabe (Enter = anlegen)…"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
                disabled={isSavingAufgabe}
              />
              <select
                value={neueAufgabePrio}
                onChange={(e) => setNeueAufgabePrio(e.target.value)}
                className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
                disabled={isSavingAufgabe}
                title="Priorität"
              >
                <option value="A">A — hoch</option>
                <option value="B">B — mittel</option>
                <option value="C">C — niedrig</option>
              </select>
              <input
                type="date"
                value={neueAufgabeFaellig}
                onChange={(e) => setNeueAufgabeFaellig(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
                disabled={isSavingAufgabe}
                title="Optionales Fälligkeitsdatum"
              />
              <button
                onClick={handleNeueAufgabe}
                disabled={isSavingAufgabe || !neueAufgabeText.trim()}
                className="flex items-center justify-center gap-1 px-4 py-2 rounded-lg text-sm font-bold text-white shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: CI.brombeer }}
              >
                {isSavingAufgabe ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Hinzufügen
              </button>
            </div>

            {offeneTodos === 0 && (
              <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                Keine offenen To-dos. 🎉
              </div>
            )}
            <TodoListe titel="Überfällig"   todos={todosGruppen.ueberfaellig} farbeText="text-red-700"   onClick={onOpenKunde} onErledigt={handleTodoErledigt} onUpdate={handleTodoUpdate} icon={AlertTriangle} />
            <TodoListe titel="Heute fällig" todos={todosGruppen.heute}        farbeText="text-amber-700" onClick={onOpenKunde} onErledigt={handleTodoErledigt} onUpdate={handleTodoUpdate} />
            <TodoListe titel="Diese Woche"  todos={todosGruppen.dieseWoche}   farbeText="text-gray-700"  onClick={onOpenKunde} onErledigt={handleTodoErledigt} onUpdate={handleTodoUpdate} />
            <TodoListe titel="Später"       todos={todosGruppen.spaeter}      farbeText="text-gray-500"  onClick={onOpenKunde} onErledigt={handleTodoErledigt} onUpdate={handleTodoUpdate} />
            <TodoListe titel="Ohne Datum"   todos={todosGruppen.ohneDatum}    farbeText="text-gray-500"  onClick={onOpenKunde} onErledigt={handleTodoErledigt} onUpdate={handleTodoUpdate} />
          </div>

          {/* Schmale Seitenleiste rechts: KundenSammel + 2 Mini-Stats gestapelt */}
          <div className="flex flex-col gap-4">
            <KundenSammelKachel
              beratungskunden={kundenZahl}
              pflegecockpitkunden={pcStats?.gesamt}
              lianeboxkunden={lianeStats?.gesamt}
            />
            <StatKachel
              icon={CheckSquare}
              farbe={CI.orchidee}
              label="Offene To-dos"
              wert={offeneTodos}
              sub={todosGruppen.ueberfaellig.length > 0
                ? `${todosGruppen.ueberfaellig.length} überfällig`
                : 'alles im Plan'}
            />
            <StatKachel
              icon={CalendarIcon}
              farbe={CI.blue}
              label="Termine diese Woche"
              wert={termineDieseWoche}
              sub={termineGruppen.heute.length > 0
                ? `${termineGruppen.heute.length} heute`
                : 'kein Termin heute'}
            />
          </div>
        </section>

        {/* ===== Karte aller Beratungskunden =====
            Pins werden nur fuer Kunden mit gecachten Koordinaten gerendert
            (Hintergrund-Geokodierung beim Anlegen + Backfill-Skript fuer
            Bestand). Klick auf einen Pin oeffnet die Akte. */}
        <KundenKarte
          kunden={alleKunden}
          onOpenKunde={onOpenKunde}
          touren={aktiveTouren}
          onAddZuTour={handleAddKundeZuTour}
          onTourenReload={() => {
            api.get('/api/touren?modus=aktiv')
              .then(r => setAktiveTouren(Array.isArray(r) ? r : []))
              .catch(() => {});
          }}
        />

        {/* ===== DSGVO: Wartet noch auf Double-Opt-In =====
            Booking-Widget-Kunden, deren DOI-Mail vermutlich im Spam-Filter
            haengt. 30 Tage Frist, danach Auto-Loeschung durch doi_cleanup-
            Daemon. Block erscheint nur, wenn etwas drinsteht. */}
        {(doiPending?.length || 0) > 0 && (
          <section className="bg-white rounded-xl shadow-sm border border-red-300 p-5">
            <h2 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4 animate-pulse" /> Wartet noch auf DSGVO-Bestätigung
              <span className="ml-2 text-xs font-normal text-gray-500 normal-case">
                ({doiPending.length} {doiPending.length === 1 ? 'Kunde' : 'Kunden'} — Double-Opt-In offen)
              </span>
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              Tipp: Die Bestätigungs-Mail landet manchmal im Spam — kurzer Anruf hilft.
              Klick auf eine Zeile öffnet die Akte.
            </p>
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
              {doiPending.slice(0, 12).map(k => {
                const fristDate = k.doiFrist ? new Date(k.doiFrist) : null;
                const tageBis = fristDate ? Math.ceil((fristDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                return (
                  <li
                    key={k.id}
                    onClick={() => onOpenKunde && onOpenKunde(k.id)}
                    className="flex items-center gap-3 px-3 py-2 bg-white hover:bg-red-50 cursor-pointer transition-colors"
                  >
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                    <span className="text-sm font-bold text-gray-800 truncate flex-1">
                      {k.lastName}, {k.firstName}
                    </span>
                    {(k.phone || k.email) && (
                      <span className="text-xs text-gray-500 truncate hidden md:inline-block max-w-[260px]">
                        {k.phone || k.email}
                      </span>
                    )}
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0 ${
                        tageBis !== null && tageBis <= 7
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {tageBis !== null
                        ? (tageBis <= 0 ? 'Frist abgelaufen' : `noch ${tageBis} Tag${tageBis === 1 ? '' : 'e'}`)
                        : 'Frist offen'}
                    </span>
                  </li>
                );
              })}
            </ul>
            {doiPending.length > 12 && (
              <div className="text-xs text-gray-500 italic mt-2">
                + {doiPending.length - 12} weitere
              </div>
            )}
          </section>
        )}

        {/* ===== Diese Woche neu hinzugekommen =====
            Termine, die in den letzten 7 Tagen angelegt wurden — egal
            ob fuer naechste Woche oder in 6 Monaten. Hilft Hendrik den
            Ueberblick, was zuletzt ins System reinkam (manuell oder
            ueber das Buchungs-Widget). */}
        {/* ===== Diese Woche neu hinzugekommen — links Termine, rechts Anfragen =====
            Beide Boxen erscheinen nebeneinander, sofern jeweils etwas drin
            ist. Wenn beides leer ist, wird die ganze Section uebersprungen. */}
        {((neueTermine?.length || 0) > 0 || (neueAnfragen?.length || 0) > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {(neueTermine?.length || 0) > 0 && (
              <section className="bg-white rounded-xl shadow-sm border border-amber-200 p-5">
                <h2 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2 text-amber-700">
                  <Sparkles className="w-4 h-4" /> Neue Termine
                  <span className="ml-2 text-xs font-normal text-gray-500 normal-case">
                    ({neueTermine.length} in den letzten 7 Tagen)
                  </span>
                </h2>
                <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                  {neueTermine.slice(0, 8).map(t => (
                    <li
                      key={t.id}
                      onClick={() => onOpenKunde && t.kundeId && onOpenKunde(t.kundeId)}
                      className="flex items-center gap-3 px-3 py-2 bg-white hover:bg-amber-50 cursor-pointer transition-colors"
                    >
                      <span className="text-xs font-bold text-gray-500 w-20 flex-shrink-0">
                        {formatTagRelativ(t.date)}
                      </span>
                      <span className="text-xs font-bold text-gray-700 w-12 flex-shrink-0">{t.time || '—'}</span>
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white flex items-center gap-1 flex-shrink-0"
                        style={{ backgroundColor: t.type === 'video' ? CI.blue : CI.green }}
                      >
                        {t.type === 'video' ? <Video className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
                        {t.type === 'video' ? 'Video' : 'vor Ort'}
                      </span>
                      <span className="text-sm font-bold text-gray-800 truncate flex-1">
                        {t.lastName}, {t.firstName}
                      </span>
                    </li>
                  ))}
                </ul>
                {neueTermine.length > 8 && (
                  <div className="text-xs text-gray-500 italic mt-2">
                    + {neueTermine.length - 8} weitere
                  </div>
                )}
              </section>
            )}

            {(neueAnfragen?.length || 0) > 0 && (
              <section className="bg-white rounded-xl shadow-sm border border-fuchsia-200 p-5">
                <h2 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: CI.brombeer }}>
                  <Inbox className="w-4 h-4" /> Neue Pflege-Cockpit-Anfragen
                  <span className="ml-2 text-xs font-normal text-gray-500 normal-case">
                    ({neueAnfragen.length} in den letzten 7 Tagen)
                  </span>
                </h2>
                <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                  {neueAnfragen.slice(0, 8).map(a => {
                    const ungelesen = a.ungelesen_count || 0;
                    return (
                      <li
                        key={a.id}
                        onClick={() => onOpenAnfrage && onOpenAnfrage(a.id)}
                        className="flex items-center gap-3 px-3 py-2 bg-white hover:bg-fuchsia-50 cursor-pointer transition-colors"
                      >
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 w-32 flex-shrink-0 truncate">
                          {a.bereich_label}
                        </span>
                        <span className={`text-sm truncate flex-1 ${ungelesen > 0 ? 'font-extrabold text-gray-900' : 'font-bold text-gray-800'}`}>
                          {a.name || a.email || a.telefon || 'Anonym'}
                        </span>
                        {ungelesen > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white bg-red-500 animate-pulse flex-shrink-0">
                            {ungelesen} neu
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400 flex-shrink-0">
                          {a.created_at ? formatTagRelativ(a.created_at.slice(0, 10)) : ''}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {neueAnfragen.length > 8 && (
                  <div className="text-xs text-gray-500 italic mt-2">
                    + {neueAnfragen.length - 8} weitere
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        {/* ===== Anstehende Termine — jetzt in voller Breite,
              weil die ToDo-Liste oben in den prominenten Bereich gewandert ist. */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: CI.brombeer }}>
            <CalendarIcon className="w-4 h-4" /> Anstehende Termine
          </h2>
          {termineGruppen.heute.length === 0 && termineGruppen.dieseWoche.length === 0 && termineGruppen.naechsteWoche.length === 0 && (
            <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              Keine Termine in den nächsten zwei Wochen.
            </div>
          )}
          <TerminListe titel="Heute"          termine={termineGruppen.heute}        farbeText="text-amber-700"  onClick={onOpenKunde} />
          <TerminListe titel="Diese Woche"    termine={termineGruppen.dieseWoche}   farbeText="text-gray-700"   onClick={onOpenKunde} />
          <TerminListe titel="Nächste Woche"  termine={termineGruppen.naechsteWoche} farbeText="text-gray-500"   onClick={onOpenKunde} />
        </section>

        {/* ===== Statistik-Sektion (on-demand laden) ===== */}
        <StatistikSektion />
      </div>
    </div>
  );
}

// ==========================================================================
// Statistik-Sektion: laedt erst nach Klick auf "Aktualisieren".
// Zeigt 4 Karten — Pflegekassen, Pflegegrade (4 Tabellen), Bundeslaender,
// Altersgruppen.
// ==========================================================================
function StatistikSektion() {
  const [stat, setStat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [stand, setStand] = useState(null);

  const laden = async () => {
    setLoading(true);
    setFehler(null);
    try {
      const data = await api.get('/api/statistik/kunden');
      setStat(data);
      setStand(new Date());
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: CI.brombeer }}>
          <BarChart3 className="w-4 h-4" /> Statistik-Auswertungen
          {stat && stand && (
            <span className="text-xs font-normal text-gray-400 normal-case tracking-normal">
              · Stand {stand.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
            </span>
          )}
        </h2>
        <button
          onClick={laden}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white shadow-sm transition-all disabled:opacity-50 hover:-translate-y-0.5"
          style={{ backgroundColor: CI.brombeer }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
          {stat ? 'Aktualisieren' : 'Statistik laden'}
        </button>
      </div>

      {fehler && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{fehler}</div>
      )}

      {!stat && !loading && !fehler && (
        <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          Klick auf <b>„Statistik laden"</b>, um die Auswertungen über deinen aktuellen Kunden-Bestand zu berechnen.
          Werden nur auf Knopfdruck erzeugt — sonst zu rechenintensiv bei jedem Page-Load.
        </div>
      )}

      {stat && (
        <div className="space-y-6">
          <div className="text-xs text-gray-500">
            Auswertung über <b>{stat.anzahl_kunden_total}</b> sichtbare Kunden.
            Die Index-Werte vergleichen den Anteil meiner Kunden mit dem
            Bevölkerungs-Anteil in Deutschland — <b>100</b> = passend
            repräsentiert, <b>&lt;100</b> unter-, <b>&gt;100</b> überrepräsentiert.
          </div>

          {/* Top 20 Pflegekassen — mit %, AOK-Aggregat hervorgehoben */}
          <StatBalkenliste
            titel="Top 20 Pflegekassen"
            items={stat.pflegekassen_top20}
            farbe={CI.brombeer}
            mitProzent
            mitAggregatStil
          />

          {/* Pflegegrade — ohne 'PG 0' und 'unbekannt', mit Prozent */}
          <PflegegradeBlock pflegegrade={stat.pflegegrade} totalKunden={stat.anzahl_kunden_total} />

          {/* Altersgruppen-Hauptgruppen — gross + farblich akzentuiert */}
          <AltersgruppenGrobBlock items={stat.altersgruppen_grob} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bundesländer mit Index, ohne unbekannt + Anzahl <= 1 */}
            <StatBalkenliste
              titel="Verteilung nach Bundesländern"
              items={(stat.bundeslaender || []).filter(b => b.name !== 'unbekannt' && b.anzahl > 1)}
              farbe={CI.blue}
              mitProzent
              mitIndex
            />

            {/* Altersgruppen 10er, ohne unbekannt + Anzahl 0 */}
            <StatBalkenliste
              titel="Altersgruppen (10er-Schritte)"
              items={(stat.altersgruppen_10er || [])
                .filter(a => a.label !== 'unbekannt' && a.anzahl > 0)
                .map(a => ({ name: a.label, anzahl: a.anzahl, anteil_pct: a.anteil_pct, bev_pct: a.bev_pct, index: a.index }))}
              farbe={CI.green}
              mitProzent
              mitIndex
            />
          </div>
        </div>
      )}
    </section>
  );
}

function StatBalkenliste({ titel, items, farbe, mitProzent, mitIndex, mitAggregatStil }) {
  if (!items || items.length === 0) {
    return (
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">{titel}</div>
        <div className="text-sm text-gray-400 italic">Keine Daten.</div>
      </div>
    );
  }
  const max = Math.max(...items.map(i => i.anzahl)) || 1;
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">{titel}</div>
      <ul className="space-y-1">
        {items.map((item, i) => {
          const breite = Math.max(2, Math.round((item.anzahl / max) * 100));
          const istAggregat = mitAggregatStil && item.ist_aggregat;
          return (
            <li
              key={i}
              className={`flex items-center gap-3 text-sm ${istAggregat ? 'bg-fuchsia-50 border border-fuchsia-200 rounded px-2 py-1 -mx-2' : ''}`}
            >
              <span className={`flex-1 truncate ${istAggregat ? 'text-fuchsia-900 font-bold' : 'text-gray-800'}`} title={item.name}>{item.name}</span>
              <div className="w-32 h-3 bg-gray-100 rounded-full overflow-hidden flex-shrink-0">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${breite}%`, backgroundColor: farbe }}
                ></div>
              </div>
              <span className={`w-20 text-right tabular-nums ${istAggregat ? 'text-fuchsia-900 font-bold' : 'font-bold text-gray-700'}`}>
                {item.anzahl}{mitProzent && item.anteil_pct != null && (
                  <span className="text-gray-400 font-normal"> ({item.anteil_pct}%)</span>
                )}
              </span>
              {mitIndex && (
                <IndexBadge index={item.index} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Index 100 = passend repraesentiert. <80 rot (unter), 80-120 grau, >120 gruen.
function IndexBadge({ index }) {
  if (index == null) {
    return <span className="w-14 text-right text-[10px] text-gray-300 tabular-nums">—</span>;
  }
  let cls = 'bg-gray-100 text-gray-600';
  if (index < 80) cls = 'bg-red-100 text-red-700';
  else if (index > 120) cls = 'bg-green-100 text-green-700';
  return (
    <span
      className={`w-14 text-center text-[10px] font-bold rounded px-1.5 py-0.5 tabular-nums ${cls}`}
      title={`Index ${index} — ${index < 80 ? 'unterrepräsentiert' : index > 120 ? 'überrepräsentiert' : 'passend zur Bevölkerung'}`}
    >
      {index}
    </span>
  );
}

// Drei prominente Balken fuer Kinder / Erwachsene / Senioren
function AltersgruppenGrobBlock({ items }) {
  if (!items || items.length === 0) return null;
  const max = Math.max(...items.map(i => i.anzahl)) || 1;
  const farben = { kinder: '#2DBAE8', erwachsene: '#85366C', senioren: '#8CC53F' };
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Altersgruppen-Hauptverteilung</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.map(item => {
          const breite = Math.max(4, Math.round((item.anzahl / max) * 100));
          const farbe = farben[item.key] || '#85366C';
          return (
            <div
              key={item.key}
              className="rounded-lg border p-3"
              style={{ borderColor: farbe + '40', backgroundColor: farbe + '0d' }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-700">{item.label}</span>
                <IndexBadge index={item.index} />
              </div>
              <div className="text-2xl font-bold text-gray-800">
                {item.anzahl}
                <span className="text-base font-normal text-gray-400 ml-2">({item.anteil_pct}%)</span>
              </div>
              <div className="mt-2 h-2 bg-white/60 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${breite}%`, backgroundColor: farbe }}></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PflegegradeBlock({ pflegegrade }) {
  const tabellen = [
    { key: 'gesamt',     label: 'Gesamt' },
    { key: 'kinder',     label: 'Bis 18 Jahre' },
    { key: 'erwachsene', label: '18–64 Jahre' },
    { key: 'senioren',   label: '65+ Jahre' },
  ];
  // Nur PG 1..5 — "PG 0" und "ohne/unbekannt" werden bewusst nicht
  // angezeigt (Wunsch von Hendrik).
  const reihenfolge = [1, 2, 3, 4, 5];
  const labels = { 1: 'PG 1', 2: 'PG 2', 3: 'PG 3', 4: 'PG 4', 5: 'PG 5' };

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Pflegegrad-Verteilung</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tabellen.map(t => {
          const data = pflegegrade[t.key] || {};
          // Bezugsgroesse: Anzahl Kunden mit echtem Pflegegrad (1..5) in dieser Tabelle.
          const total = reihenfolge.reduce((s, k) => s + (data[k] || 0), 0);
          return (
            <div key={t.key} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 flex items-center justify-between">
                <span>{t.label}</span>
                <span className="text-gray-400 font-normal normal-case tracking-normal">∑ {total}</span>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {reihenfolge.map(k => {
                    const wert = data[k] || 0;
                    const pct = total > 0 ? Math.round((wert / total) * 1000) / 10 : 0;
                    return (
                      <tr key={k}>
                        <td className="py-0.5">{labels[k]}</td>
                        <td className="py-0.5 text-right tabular-nums">
                          <span className="font-bold">{wert}</span>
                          {total > 0 && <span className="text-gray-400 font-normal ml-1">({pct}%)</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatKachel({ icon: Icon, farbe, label, wert, sub }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: farbe }}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">{label}</div>
        <div className="text-3xl font-bold text-gray-800 leading-tight">{wert}</div>
        <div className="text-xs text-gray-400">{sub}</div>
      </div>
    </div>
  );
}

// "Aktuelle Kunden" — Sammel-Kachel mit drei Geschaeftsbereich-Zaehlern.
// Liane-Box-Zaehler kommt aus dem echten Datenpool (Tabelle
// liane_box_kunden, Endpoint /api/liane/kunden/stats), seit dem
// Excel-Import der ~849 Bestandskunden.
function KundenSammelKachel({ beratungskunden, pflegecockpitkunden, lianeboxkunden }) {
  const fmt = (n) => (n == null ? '—' : n.toLocaleString('de-DE'));
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-start gap-4">
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0"
        style={{ backgroundColor: CI.brombeer }}
      >
        <Users className="w-6 h-6" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">
          Aktuelle Kunden
        </div>
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-gray-600 flex items-center gap-1.5 truncate">
              <Users className="w-3.5 h-3.5" style={{ color: CI.brombeer }} /> Beratungskunden
            </span>
            <span className="text-lg font-bold text-gray-800 tabular-nums">{fmt(beratungskunden)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-gray-600 flex items-center gap-1.5 truncate">
              <Activity className="w-3.5 h-3.5" style={{ color: CI.blue }} /> Pflegecockpit-Kunden
            </span>
            <span className="text-lg font-bold text-gray-800 tabular-nums">{fmt(pflegecockpitkunden)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-gray-600 flex items-center gap-1.5 truncate">
              <Package className="w-3.5 h-3.5" style={{ color: CI.green }} /> Liane-Box-Kunden
            </span>
            <span className="text-lg font-bold text-gray-800 tabular-nums">{fmt(lianeboxkunden)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TerminListe({ titel, termine, farbeText, onClick }) {
  if (!termine || termine.length === 0) return null;
  return (
    <div className="mb-4">
      <div className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${farbeText}`}>
        {titel} ({termine.length})
      </div>
      <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
        {termine.map(t => (
          <li
            key={t.id}
            onClick={() => onClick && t.kundeId && onClick(t.kundeId)}
            className="flex items-center gap-3 px-3 py-2 bg-white hover:bg-fuchsia-50 cursor-pointer transition-colors"
          >
            <span className="text-xs font-bold text-gray-500 w-20 flex-shrink-0">
              {formatTagRelativ(t.date)}
            </span>
            <span className="text-xs font-bold text-gray-700 w-12 flex-shrink-0">{t.time || '—'}</span>
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white flex items-center gap-1 flex-shrink-0"
              style={{ backgroundColor: t.type === 'video' ? CI.blue : CI.green }}
            >
              {t.type === 'video' ? <Video className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
              {t.type === 'video' ? 'Video' : 'vor Ort'}
            </span>
            <span className="text-sm font-bold text-gray-800 truncate">
              {t.lastName}, {t.firstName}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Prioritaets-Stile + Rotation A -> B -> C -> A (Klick-Toggle).
const PRIO_STYLE_HOME = {
  A: { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },
  B: { bg: '#E5E7EB', text: '#374151', border: '#D1D5DB' },
  C: { bg: '#F3F4F6', text: '#9CA3AF', border: '#E5E7EB' },
};
const PRIO_NEXT_HOME = { A: 'B', B: 'C', C: 'A' };

function TodoListe({ titel, todos, farbeText, onClick, onErledigt, onUpdate, icon: Icon }) {
  if (!todos || todos.length === 0) return null;
  return (
    <div className="mb-4">
      <div className={`text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1 ${farbeText}`}>
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {titel} ({todos.length})
      </div>
      <ul className="space-y-1">
        {todos.map(t => (
          <TodoZeileHome
            key={t.id}
            todo={t}
            onClick={onClick}
            onErledigt={onErledigt}
            onUpdate={onUpdate}
          />
        ))}
      </ul>
    </div>
  );
}

function TodoZeileHome({ todo, onClick, onErledigt, onUpdate }) {
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(todo.text || '');
  const [editFaellig, setEditFaellig] = useState(todo.faellig_am || '');
  const [editPrio, setEditPrio] = useState((todo.prioritaet || 'B').toUpperCase());
  const [isSavingZeile, setIsSavingZeile] = useState(false);

  const prio = (todo.prioritaet || 'B').toUpperCase();
  const prioStyle = PRIO_STYLE_HOME[prio] || PRIO_STYLE_HOME.B;

  const startEdit = (e) => {
    e.stopPropagation();
    setEditText(todo.text || '');
    setEditFaellig(todo.faellig_am || '');
    setEditPrio((todo.prioritaet || 'B').toUpperCase());
    setEditMode(true);
  };

  const speichern = async () => {
    const text = (editText || '').trim();
    if (!text) { alert('Text darf nicht leer sein.'); return; }
    setIsSavingZeile(true);
    try {
      await onUpdate(todo.id, {
        text,
        faellig_am: editFaellig || null,
        prioritaet: editPrio,
      });
      setEditMode(false);
    } catch { /* onUpdate hat schon Fehler-Alert */ }
    finally { setIsSavingZeile(false); }
  };

  const prioToggeln = (e) => {
    e.stopPropagation();
    if (!onUpdate) return;
    onUpdate(todo.id, { prioritaet: PRIO_NEXT_HOME[prio] || 'B' });
  };

  const verschiebeAufMorgen = (e) => {
    e.stopPropagation();
    if (!onUpdate) return;
    const morgen = new Date();
    morgen.setDate(morgen.getDate() + 1);
    const isoMorgen = morgen.toISOString().slice(0, 10);
    onUpdate(todo.id, { faellig_am: isoMorgen });
  };

  if (editMode) {
    return (
      <li className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 bg-fuchsia-50 border border-fuchsia-200 rounded">
        <input
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); speichern(); } if (e.key === 'Escape') setEditMode(false); }}
          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          autoFocus
        />
        <select
          value={editPrio}
          onChange={(e) => setEditPrio(e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
          title="Priorität"
        >
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </select>
        <input
          type="date"
          value={editFaellig}
          onChange={(e) => setEditFaellig(e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
        />
        <button
          onClick={speichern}
          disabled={isSavingZeile}
          className="text-xs font-bold px-3 py-1 rounded text-white bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50"
        >
          {isSavingZeile ? '…' : 'Speichern'}
        </button>
        <button
          onClick={() => setEditMode(false)}
          className="text-xs font-bold px-3 py-1 rounded text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
        >
          Abbrechen
        </button>
      </li>
    );
  }

  const istAllgemein = !todo.kunde_id;
  const klickbar = !!todo.kunde_id && !!onClick;
  return (
    <li
      onClick={klickbar ? () => onClick(todo.kunde_id) : undefined}
      className={`flex items-start gap-3 px-3 py-2 bg-white border border-gray-100 rounded transition-colors ${klickbar ? 'hover:bg-fuchsia-50 cursor-pointer' : ''}`}
    >
      {onErledigt && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onErledigt(todo.id); }}
          className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-green-300 bg-white text-green-600 hover:bg-green-500 hover:border-green-500 hover:text-white flex items-center justify-center transition-colors"
          title="Als erledigt markieren"
          aria-label="Als erledigt markieren"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={prioToggeln}
        className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold border transition-transform hover:scale-110"
        style={{ backgroundColor: prioStyle.bg, color: prioStyle.text, borderColor: prioStyle.border }}
        title={`Priorität ${prio} — Klick für nächste Stufe`}
      >
        {prio}
      </button>
      <div className="flex-1">
        <div className="text-sm text-gray-800">{todo.text}</div>
        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
          {istAllgemein ? (
            <span className="inline-block px-1.5 py-0.5 rounded text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">Allgemein</span>
          ) : (
            <span>{todo.kunde_name || '—'}</span>
          )}
          {todo.faellig_am && (
            <span className="text-gray-400">· {formatTagRelativ(todo.faellig_am)}</span>
          )}
        </div>
      </div>
      {onUpdate && (
        <button
          type="button"
          onClick={verschiebeAufMorgen}
          className="flex-shrink-0 text-gray-300 hover:text-amber-600 transition-colors"
          title="Auf morgen verschieben"
          aria-label="Auf morgen verschieben"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
      {onUpdate && (
        <button
          type="button"
          onClick={startEdit}
          className="flex-shrink-0 text-gray-300 hover:text-fuchsia-600 transition-colors"
          title="Bearbeiten"
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      )}
    </li>
  );
}
